import React from 'react';
import {
  ArrowRight,
  Link2,
  SearchCheck,
  LayoutDashboard,
} from 'lucide-react';
import { integrationsWithLogos } from './Integrations';

const logoByName = integrationsWithLogos.reduce((acc, integration) => {
  acc[integration.name] = integration.image;
  return acc;
}, {});

const steps = [
  {
    Icon: Link2,
    title: 'Connect your accounts',
    description:
      'Connect Shopify, Square, Boulevard, and your Meta/Google ad accounts in minutes. Read-only and secure.',
    logos: ['Shopify', 'Square', 'Boulevard', 'Meta', 'Google Ads'],
    accent: {
      icon: 'bg-gradient-to-br from-sky-100 to-sky-50 text-sky-900 ring-sky-200/90',
      card: 'border-sky-200/70 bg-gradient-to-br from-white to-sky-50/50',
    },
  },
  {
    Icon: SearchCheck,
    title: 'Scan the last 30 days',
    description:
      'Kolanut matches spend against outcomes and surfaces campaigns, ad sets, and channels that are burning budget.',
    logos: ['Meta', 'Google Ads'],
    accent: {
      icon: 'bg-gradient-to-br from-violet-100 to-violet-50 text-violet-900 ring-violet-200/90',
      card: 'border-violet-200/70 bg-gradient-to-br from-white to-violet-50/40',
    },
  },
  {
    Icon: LayoutDashboard,
    title: 'Get an action-ready report',
    description:
      'See exactly what to pause, what to fix, and where to move budget. No auto-execution. You stay in control.',
    logos: [],
    actions: ['Fix now', 'Pause losers', 'Reallocate budget'],
    accent: {
      icon: 'bg-gradient-to-br from-amber-100 to-amber-50 text-amber-950 ring-amber-200/90',
      card: 'border-amber-200/70 bg-gradient-to-br from-white to-amber-50/45',
    },
  },
];

const TabbedFeaturesSection = () => {
  return (
    <section id="tabbed-features" className="py-16 md:py-28 bg-gradient-to-b from-stone-50/90 via-white to-slate-50/40 overflow-x-hidden">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-stone-600 mb-3 md:mb-4">
            How it works
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-stone-900 leading-[1.12]">
            Find waste fast.
            {' '}
            <span className="bg-gradient-to-r from-[#6ee7a8] via-[#8ae690] to-[#5ec269] bg-clip-text text-transparent">
              Keep control.
            </span>
          </h2>
        </div>

        <div className="grid gap-6 sm:gap-7 lg:gap-8 md:grid-cols-1 lg:grid-cols-3">
          {steps.map(({ Icon, title, description, accent, logos, actions }) => (
            <article
              key={title}
              className={`group relative flex flex-col gap-6 rounded-3xl border p-7 sm:p-8 md:p-9 lg:p-10 min-h-[23rem] sm:min-h-[24rem] shadow-[0_22px_60px_-28px_rgba(15,23,42,0.14)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_28px_72px_-32px_rgba(15,23,42,0.2)] ${accent.card}`}
            >
              <div
                className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] ring-1 sm:h-[4.5rem] sm:w-[4.5rem] md:h-20 md:w-20 ${accent.icon}`}
                aria-hidden
              >
                <Icon className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" strokeWidth={1.55} aria-hidden />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <h3 className="text-xl sm:text-2xl font-semibold text-stone-900 tracking-tight">{title}</h3>
                <p className="mt-3 text-base sm:text-lg text-stone-600 leading-relaxed">{description}</p>
                <div className="mt-5 flex flex-wrap items-center gap-2.5">
                  {logos.map((name) => {
                    const src = logoByName[name];
                    if (!src) return null;
                    return (
                      <span
                        key={name}
                        className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 shadow-sm"
                      >
                        <img
                          src={src}
                          alt={`${name} logo`}
                          className="h-9 w-auto object-contain"
                        />
                        <span className="text-xs font-medium text-stone-700">{name}</span>
                      </span>
                    );
                  })}
                  {actions?.length ? (
                    <>
                      <span className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 shadow-sm">
                        <img
                          src="/kolanut_logo.png"
                          alt="Kolanut logo"
                          className="h-9 w-9 object-contain"
                        />
                        <span className="text-xs font-medium text-stone-700">Kolanut</span>
                      </span>
                      {actions.map((action) => (
                        <span
                          key={action}
                          className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800"
                        >
                          {action}
                        </span>
                      ))}
                    </>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 md:mt-10 rounded-3xl border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/40 p-6 sm:p-8 shadow-[0_22px_60px_-28px_rgba(15,23,42,0.14)]">
          <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base text-stone-700 font-medium">
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">Your POS</span>
            <ArrowRight className="h-4 w-4 text-stone-400" aria-hidden />
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">Your online store</span>
            <ArrowRight className="h-4 w-4 text-stone-400" aria-hidden />
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">Your CRM/booking tool</span>
            <ArrowRight className="h-4 w-4 text-stone-400" aria-hidden />
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">Meta / Google</span>
          </div>
          <div className="mt-5 rounded-2xl border border-red-200/70 bg-red-50/70 p-4 sm:p-5">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-red-700">
              Sample result
            </p>
            <div className="mt-3 rounded-xl border border-stone-200 bg-white">
              <div className="sm:hidden divide-y divide-stone-100">
                <div className="p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-stone-900">
                    <img src="/facebook-logo.png" alt="Facebook logo" className="h-9 w-9 object-contain" />
                    FB Prospecting - Broad
                  </div>
                  <p className="mt-2 text-sm text-stone-700">Spend: $2,847 | Revenue: $8,200 | ROAS: 2.9x</p>
                  <p className="mt-1 text-sm font-medium text-emerald-700">Keep</p>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-stone-900">
                    <img src="/facebook-logo.png" alt="Facebook logo" className="h-9 w-9 object-contain" />
                    FB Retargeting - Image
                  </div>
                  <p className="mt-2 text-sm text-stone-700">Spend: $1,203 | Revenue: $421 | ROAS: 0.35x</p>
                  <p className="mt-1 text-sm font-medium text-red-700">Waste ($782)</p>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-stone-900">
                    <img src="/integrations/google_ads.png" alt="Google Ads logo" className="h-9 w-9 object-contain" />
                    Google Branded
                  </div>
                  <p className="mt-2 text-sm text-stone-700">Spend: $890 | Revenue: $3,100 | ROAS: 3.5x</p>
                  <p className="mt-1 text-sm font-medium text-emerald-700">Keep</p>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-stone-900">
                    <img src="/integrations/google_ads.png" alt="Google Ads logo" className="h-9 w-9 object-contain" />
                    Google Display
                  </div>
                  <p className="mt-2 text-sm text-stone-700">Spend: $450 | Revenue: $89 | ROAS: 0.20x</p>
                  <p className="mt-1 text-sm font-medium text-red-700">Waste ($361)</p>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-stone-900">
                    <img src="/instagram-logo.png" alt="Instagram logo" className="h-9 w-9 object-contain" />
                    IG Stories - Summer
                  </div>
                  <p className="mt-2 text-sm text-stone-700">Spend: $612 | Revenue: $134 | ROAS: 0.22x</p>
                  <p className="mt-1 text-sm font-medium text-red-700">Waste ($478)</p>
                </div>
              </div>

              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-[760px] w-full text-left">
                  <thead className="bg-stone-50">
                    <tr className="text-xs sm:text-sm text-stone-600">
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Campaign</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Spend</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Revenue</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">ROAS</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Verdict</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm sm:text-base text-stone-800">
                    <tr className="border-t border-stone-100">
                      <td className="px-3 sm:px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <img src="/facebook-logo.png" alt="Facebook logo" className="h-9 w-9 object-contain" />
                          FB Prospecting - Broad
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5">$2,847</td>
                      <td className="px-3 sm:px-4 py-2.5">$8,200</td>
                      <td className="px-3 sm:px-4 py-2.5">2.9x</td>
                      <td className="px-3 sm:px-4 py-2.5 text-emerald-700 font-medium">Keep</td>
                    </tr>
                    <tr className="border-t border-stone-100">
                      <td className="px-3 sm:px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <img src="/facebook-logo.png" alt="Facebook logo" className="h-9 w-9 object-contain" />
                          FB Retargeting - Image
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5">$1,203</td>
                      <td className="px-3 sm:px-4 py-2.5">$421</td>
                      <td className="px-3 sm:px-4 py-2.5">0.35x</td>
                      <td className="px-3 sm:px-4 py-2.5 text-red-700 font-medium">Waste ($782)</td>
                    </tr>
                    <tr className="border-t border-stone-100">
                      <td className="px-3 sm:px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <img src="/integrations/google_ads.png" alt="Google Ads logo" className="h-9 w-9 object-contain" />
                          Google Branded
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5">$890</td>
                      <td className="px-3 sm:px-4 py-2.5">$3,100</td>
                      <td className="px-3 sm:px-4 py-2.5">3.5x</td>
                      <td className="px-3 sm:px-4 py-2.5 text-emerald-700 font-medium">Keep</td>
                    </tr>
                    <tr className="border-t border-stone-100">
                      <td className="px-3 sm:px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <img src="/integrations/google_ads.png" alt="Google Ads logo" className="h-9 w-9 object-contain" />
                          Google Display
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5">$450</td>
                      <td className="px-3 sm:px-4 py-2.5">$89</td>
                      <td className="px-3 sm:px-4 py-2.5">0.20x</td>
                      <td className="px-3 sm:px-4 py-2.5 text-red-700 font-medium">Waste ($361)</td>
                    </tr>
                    <tr className="border-t border-stone-100">
                      <td className="px-3 sm:px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <img src="/instagram-logo.png" alt="Instagram logo" className="h-9 w-9 object-contain" />
                          IG Stories - Summer
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5">$612</td>
                      <td className="px-3 sm:px-4 py-2.5">$134</td>
                      <td className="px-3 sm:px-4 py-2.5">0.22x</td>
                      <td className="px-3 sm:px-4 py-2.5 text-red-700 font-medium">Waste ($478)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-red-200 bg-red-100 px-3 py-1.5 text-sm sm:text-base font-semibold text-red-800">
              Total waste identified: $1,621
            </p>
            <div className="mt-4 flex justify-center">
              <a
                href="https://engagement.kolanut.ai/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-12 md:h-14 px-8 md:px-10 lg:px-14 rounded-full bg-[#8ae690] text-black font-semibold text-sm md:text-base shadow-md hover:bg-[#7ad67f] hover:shadow-lg transition-all duration-200"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70">
                  <img
                    src="/kolanut_logo.png"
                    alt="Kolanut logo"
                    className="w-4 h-4 object-contain"
                  />
                </span>
                Try Kolanut now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TabbedFeaturesSection;
